import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import browserHistory from '../../history';
import Header from '../authentication/Header';
import Footer from '../authentication/Footer';
import Loader from '../Loader';

const getBase = () => (window.site && window.site.screeningApiBaseUrl) || 'https://content-screening-api.dev2.envoi.cloud';
const getApp = () => (window.app && window.app.appName) || 'kyliejenner';
const getUrlApp = () => (window.app && (window.app.urlAppName || window.app.appName)) || 'kyliejenner';
const getEnv = () => (window.app && window.app.appEnvName) || 'dev';
const getToken = () => (window.app && window.app.storage && window.app.storage.getItem('token')) || '';

function getAssetImageUrl(asset) {
  const config = (window.site && window.site.config) || {};
  const cdn = config.sourceCloudFront || '';
  const autoCompress = '?auto=compress,format&width=300';
  const portraitPath = asset?.destinations?.uploads?.envoi?.envoi_portrait?.filePath;
  if (cdn && portraitPath) return cdn + '/' + portraitPath.replace(/ /g, '%20') + autoCompress;
  if (asset?.thumbnail) return asset.thumbnail.replace(/ /g, '%20');
  return ((asset?.portrait_logo_image || config.defaultLandscape || '') + autoCompress).replace(/ /g, '%20');
}

function getTitle(asset) {
  return asset?.assetname || asset?.title || asset?.name || '';
}

// ── Single group carousel ──────────────────────────────────────────────────
function ArtistCarousel({ groupName, artistId }) {
  const trackRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const pendingScrollRef = useRef(false);

  const buildUrl = (page) => {
    const loc = (window.app && window.app.locationData) || {};
    let url = `${getBase()}/assets?appname=${getApp()}&env=${getEnv()}&token=${getToken()}&carousel=${encodeURIComponent(groupName)}&artistId=${artistId}&assetcount=12&pageNumber=${page}&scrubbing=true&appType=screening`;
    if (loc.country) url += `&country=${encodeURIComponent(loc.country)}`;
    if (loc.region) url += `&region=${encodeURIComponent(loc.region)}`;
    if (loc.city) url += `&city=${encodeURIComponent(loc.city)}`;
    if (loc.timezone) url += `&timezone=${encodeURIComponent(loc.timezone)}`;
    return url;
  };

  useEffect(() => {
    axios.post(buildUrl(1))
      .then(res => {
        const items = res.data?.result?.Items || [];
        const total = res.data?.result?.assetTotal || 0;
        setAssets(items);
        if (items.length < 12 || items.length >= total) hasMoreRef.current = false;
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (loading) return;
    const el = trackRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(hasMoreRef.current || el.scrollLeft + el.offsetWidth < el.scrollWidth - 10);
    };
    el.addEventListener('scroll', check, { passive: true });
    check();
    return () => el.removeEventListener('scroll', check);
  }, [loading]);

  const loadMore = () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    axios.post(buildUrl(nextPage))
      .then(res => {
        const more = res.data?.result?.Items || [];
        if (more.length > 0) {
          setAssets(prev => [...prev, ...more]);
          pageRef.current = nextPage;
          if (more.length < 12) hasMoreRef.current = false;
        } else {
          hasMoreRef.current = false;
        }
      })
      .catch(() => { hasMoreRef.current = false; })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const el = trackRef.current;
          if (!el) return;
          setCanScrollRight(hasMoreRef.current || el.scrollLeft + el.offsetWidth < el.scrollWidth - 10);
          if (pendingScrollRef.current) {
            pendingScrollRef.current = false;
            el.scrollBy({ left: el.offsetWidth, behavior: 'smooth' });
          }
        }));
      });
  };

  const scroll = (dir) => {
    if (!trackRef.current || loading) return;
    const { offsetWidth, scrollLeft, scrollWidth } = trackRef.current;
    if (dir > 0 && scrollLeft + offsetWidth >= scrollWidth - 10) {
      pendingScrollRef.current = true;
      loadMore();
      return;
    }
    trackRef.current.scrollBy({ left: dir * offsetWidth, behavior: 'smooth' });
  };

  const skeletons = Array.from({ length: 6 });
  if (!loading && !assets.length) return null;

  return (
    <div className="home-row">
      <h2 className="row-title">{groupName}</h2>
      <div className="row-wrapper">
        <button className="row-arrow row-arrow--left" onClick={() => scroll(-1)} disabled={!canScrollLeft}>
          <i className="material-icons">chevron_left</i>
        </button>
        <div className="row-track" ref={trackRef}>
          {loading
            ? skeletons.map((_, i) => (
                <div key={i} className="row-card row-card--skeleton">
                  <div className="row-card-img-wrap" />
                </div>
              ))
            : assets.map((asset, i) => (
                <div
                  key={asset._id || i}
                  className="row-card"
                  onClick={() => browserHistory.push({ pathname: '/asset', search: `?assetid=${asset.assetid}&appname=${getUrlApp()}`, state: { assetType: asset.assetType } })}
                >
                  <div className="row-card-img-wrap">
                    <img src={getAssetImageUrl(asset)} alt={getTitle(asset)} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                    {asset?.isMembersOnly && <span className="row-card-badge">Members Only</span>}
                  </div>
                  <p className="row-card-title" title={getTitle(asset)}>{getTitle(asset)}</p>
                </div>
              ))
          }
          {loadingMore && skeletons.map((_, i) => (
            <div key={`more-${i}`} className="row-card row-card--skeleton">
              <div className="row-card-img-wrap" />
            </div>
          ))}
        </div>
        <button className="row-arrow row-arrow--right" onClick={() => scroll(1)} disabled={!canScrollRight}>
          <i className="material-icons">chevron_right</i>
        </button>
      </div>
    </div>
  );
}

// ── Artist Detail Page ─────────────────────────────────────────────────────
function getArtistProfileImage(artist) {
  const config = (window.site && window.site.config) || {};
  const cdn = config.sourceCloudFront || '';
  const img = artist?.portrait_logo_image || artist?.landscape_image || artist?.thumbnail || '';
  if (!img) return config.defaultLandscape || '';
  if (img.startsWith('http')) return img.replace(/ /g, '%20');
  return (cdn ? cdn + '/' + img.replace(/ /g, '%20') : img.replace(/ /g, '%20'));
}

function getSocialLinks(artist) {
  return [
    { platform: 'youtube',   url: artist?.youtube_link   || artist?.youtubeLink   || artist?.youtube   || artist?.links?.youtube   || '' },
    { platform: 'facebook',  url: artist?.facebook_link  || artist?.facebookLink  || artist?.facebook  || artist?.links?.facebook  || '' },
    { platform: 'twitter',   url: artist?.twitter_link   || artist?.twitterLink   || artist?.twitter   || artist?.links?.twitter   || '' },
    { platform: 'instagram', url: artist?.instagram_link || artist?.instagramLink || artist?.instagram || artist?.links?.instagram || '' },
  ].filter(s => s.url);
}

const SOCIAL_COLORS = { youtube: '#FF0000', facebook: '#1877F2', twitter: '#1DA1F2', instagram: '#E1306C' };
const SOCIAL_ICONS  = { youtube: 'smart_display', facebook: 'group', twitter: 'alternate_email', instagram: 'camera_alt' };

export default function HeirloomArtistDetailPage({ location }) {
  const params = new URLSearchParams(location && location.search);
  const artistId = params.get('artistId') || params.get('artistid');

  const [artist, setArtist] = useState(null);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!artistId) { setIsLoading(false); return; }

    const detailUrl = `${getBase()}/getArtists?appname=${getApp()}&env=${getEnv()}&token=${getToken()}&artist_id=${artistId}`;
    const groupsUrl = `${getBase()}/assets?appname=${getApp()}&token=${getToken()}&carousel=creator&artistId=${artistId}&clientType=tvos&env=${getEnv()}&getGroups=true`;

    Promise.all([axios.get(detailUrl), axios.post(groupsUrl)])
      .then(([detailRes, groupsRes]) => {
        const rawArtist = detailRes.data?.result?.data?.[0] || detailRes.data?.result?.[0] || detailRes.data?.result || detailRes.data?.data?.[0] || null;
        setArtist(rawArtist);
        setIsFollowing(!!(rawArtist?.follow));

        const raw = groupsRes.data?.result || [];
        const all = Array.isArray(raw) ? raw : [];
        const filtered = all.filter(g => {
          const year = parseInt(g?.name || g?.key || '0', 10);
          return year > 1999;
        });
        setGroups(filtered);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [artistId]);

  const getGroupName = (g) => g?.name || g?.key || g?.groupname || '';

  const displayName = artist
    ? ((artist?.firstname || '') + ' ' + (artist?.lastname || '')).trim() || artist?.name || artist?.title || ''
    : (location?.state?.artistName || '');

  const followers = artist?.followers || artist?.followersCount || artist?.followerCount || 0;
  const description = artist?.description || artist?.about || artist?.bio || '';
  const creatorType = artist?.creatorType || artist?.creator_type || '';
  const socialLinks = artist ? getSocialLinks(artist.socialLinks) : [];

  const stats = [
    { label: 'Channels',  value: artist?.channelCount  ?? artist?.channels  ?? 0 },
    { label: 'Videos',    value: artist?.videoCount    ?? artist?.videos    ?? 0 },
    { label: 'Brands',    value: artist?.brandCount    ?? artist?.brands    ?? 0 },
    { label: 'Events',    value: artist?.eventCount    ?? artist?.events    ?? 0 },
    { label: 'Products',  value: artist?.productCount  ?? artist?.products  ?? 0 },
  ];

  const joinedDate = artist?.created || '';
  const formattedJoined = joinedDate ? new Date(joinedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const views  = artist?.views  || artist?.viewCount  || 0;
  const likes  = artist?.likes  || artist?.likeCount  || 0;
  const dislikes = artist?.dislikes || artist?.dislikeCount || 0;

  const STAT_ICON = { Channels: 'live_tv', Videos: 'play_circle', Brands: 'storefront', Events: 'event', Products: 'inventory_2' };

  const siteConfig = (window.site && window.site.config) || {};
  const creatorCode = artist?.code || artist?.artistid || artistId || '';
  const shareUrl = (siteConfig.webAppURL || window.location.origin) + '?creator=' + creatorCode;
  const isMobile = window.innerWidth <= 768;

  const handleFollow = () => {
    const token = getToken();
    if (!token) {
      const cognito = (siteConfig.authentication && siteConfig.authentication.cognito) || {};
      const { clientId, loginCognitoUrl, cognitoTokenType = 'token' } = cognito;
      if (loginCognitoUrl && clientId) {
        sessionStorage.setItem('preLoginUrlAppName', getUrlApp());
        window.location = `${loginCognitoUrl}?client_id=${clientId}&response_type=${cognitoTokenType}&scope=aws.cognito.signin.user.admin+email+openid+phone+profile&redirect_uri=${window.location.origin}`;
      }
      return;
    }
    if (!creatorCode) return;
    const url = `${getBase()}/follow?appname=${getApp()}&env=${getEnv()}&token=${token}&code=${creatorCode}`;
    setIsFollowing(prev => !prev);
    axios.post(url).catch(() => setIsFollowing(prev => !prev));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const SHARE_OPTIONS = [
    { label: 'Email',    icon: 'email',    color: '#555',    href: `mailto:?subject=${encodeURIComponent(displayName)}&body=${encodeURIComponent(shareUrl)}` },
    { label: 'Facebook', icon: 'facebook', color: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { label: 'Twitter',  icon: 'twitter',  color: '#1DA1F2', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(displayName)}` },
    { label: 'Linked In',icon: 'linkedin', color: '#0A66C2', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
  ];

  const SHARE_ICON_SVG = {
    email:    <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/></svg>,
    facebook: <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>,
    twitter:  <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/></svg>,
    linkedin: <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>,
  };

  return (
    <React.Fragment>
      <div className="home-page">
        {isLoading && <Loader />}
        <Header />
        <main className="home-main" style={{ paddingTop: '70px' }}>

          {/* ── Profile Header ─────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 20, padding: isMobile ? '20px 16px 16px' : '28px 48px 24px', background: 'linear-gradient(135deg,rgba(255,255,255,0.04) 0%,transparent 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <img
              src={getArtistProfileImage(artist)}
              alt={displayName}
              style={{ width: isMobile ? 80 : 110, height: isMobile ? 80 : 110, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '3px solid rgba(255,255,255,0.15)', background: '#1a1a1a' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 34, fontWeight: 700, letterSpacing: '-0.5px' }}>{displayName}</h1>
              {creatorType && <p style={{ margin: '6px 0 0', color: '#aaa', fontSize: isMobile ? 13 : 16, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{creatorType}</p>}
              <p style={{ margin: '4px 0 0', color: '#777', fontSize: isMobile ? 13 : 14 }}>{followers} Followers</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0 }}>
              <button
                onClick={handleFollow}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: isMobile ? '6px 12px' : '8px 20px', borderRadius: 24,
                  border: '1.5px solid rgba(255,255,255,0.5)',
                  background: isFollowing ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: '#fff', fontSize: isMobile ? 13 : 15, fontWeight: 600, cursor: 'pointer',
                  letterSpacing: '0.04em', transition: 'all 0.2s', boxSizing: 'border-box', whiteSpace: 'nowrap',
                }}
              >
                <i className="material-icons" style={{ fontSize: isMobile ? 16 : 20 }}>{isFollowing ? 'person_remove' : 'person_add'}</i>
                {isFollowing ? 'UN FOLLOW' : 'FOLLOW'}
              </button>
              <button
                onClick={() => setShowShare(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 38, height: 38, minWidth: 38, minHeight: 38,
                  borderRadius: '50%', flexShrink: 0, padding: 0,
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  background: 'transparent', color: '#fff', cursor: 'pointer',
                  transition: 'background 0.2s', boxSizing: 'border-box', lineHeight: 1,
                }}
              >
                <i className="material-icons" style={{ fontSize: 18, lineHeight: 1, display: 'block' }}>share</i>
              </button>
            </div>
          </div>

          {/* ── Share Modal ───────────────────────────── */}
          {showShare && (
            <div
              onClick={() => setShowShare(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: '#1e1e1e', borderRadius: 12, padding: isMobile ? '20px 16px 16px' : '28px 32px 24px', width: 440, maxWidth: '92vw', position: 'relative' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Share</h3>
                  <button onClick={() => setShowShare(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4 }}>
                    <i className="material-icons" style={{ fontSize: 24 }}>close</i>
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 24 }}>
                  {SHARE_OPTIONS.map(opt => (
                    <a
                      key={opt.label}
                      href={opt.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#fff' }}
                    >
                      <span style={{ width: isMobile ? 52 : 64, height: isMobile ? 52 : 64, borderRadius: '50%', background: opt.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {SHARE_ICON_SVG[opt.icon]}
                      </span>
                      <span style={{ fontSize: 13 }}>{opt.label}</span>
                    </a>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, overflow: 'hidden', background: 'rgba(255,255,255,0.04)' }}>
                  <span style={{ flex: 1, padding: '10px 14px', fontSize: 13, color: '#bbb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{shareUrl}</span>
                  <button
                    onClick={handleCopy}
                    style={{ padding: '10px 16px', background: 'none', border: 'none', color: copied ? '#4caf50' : '#4ea8de', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── 2-column body ─────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 0, padding: '0 0 40px' }}>

            {/* Left: description + connect */}
            <div style={{ flex: 1, padding: isMobile ? '20px 16px 0' : '28px 48px 0' }}>
              {description && (
                <div style={{ marginBottom: 28 }}>
                  <h3 style={{ margin: '0 0 14px', fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#e5e5e5', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 10 }}>Description</h3>
                  <p style={{ color: '#b0b0b0', lineHeight: '24px', fontSize: isMobile ? 14 : 16, margin: 0, letterSpacing: '0.3px' }}>{description}</p>
                </div>
              )}
              {socialLinks.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 16px', fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#e5e5e5', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 10 }}>Connect</h3>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {socialLinks.map(({ platform, url }) => (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={platform}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: isMobile ? 44 : 50, height: isMobile ? 44 : 50, borderRadius: '50%',
                          background: SOCIAL_COLORS[platform] || '#444',
                          color: '#fff', textDecoration: 'none',
                          boxShadow: `0 4px 12px ${SOCIAL_COLORS[platform]}55`,
                          transition: 'transform 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <i className="material-icons" style={{ fontSize: isMobile ? 20 : 24 }}>{SOCIAL_ICONS[platform] || 'link'}</i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {!description && !socialLinks.length && !isLoading && (
                <p style={{ color: '#555', fontStyle: 'italic', fontSize: 16 }}>No details available.</p>
              )}
            </div>

            {/* Right: stats card */}
            <div style={{ width: isMobile ? 'auto' : 220, flexShrink: 0, padding: isMobile ? '20px 16px 0' : '24px 24px 0 16px' }}>
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '16px 14px 14px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#e5e5e5', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stats</h3>
                {formattedJoined && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color: '#b0b0b0', fontSize: 14 }}>
                    <i className="material-icons" style={{ fontSize: 15, color: '#777' }}>calendar_today</i>
                    {formattedJoined}
                  </div>
                )}
                {views > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, color: '#b0b0b0', fontSize: 14 }}>
                    <i className="material-icons" style={{ fontSize: 15, color: '#777' }}>visibility</i>
                    {views.toLocaleString()} views
                  </div>
                )}
                {(likes > 0 || dislikes > 0) && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 20, padding: '4px 10px', fontSize: 14, color: '#ccc' }}>
                      <i className="material-icons" style={{ fontSize: 15, color: '#4caf50' }}>thumb_up</i> {likes}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 20, padding: '4px 10px', fontSize: 14, color: '#ccc' }}>
                      <i className="material-icons" style={{ fontSize: 15, color: '#f44336' }}>thumb_down</i> {dislikes}
                    </span>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3,1fr)' : '1fr 1fr', gap: 7 }}>
                  {stats.map(s => (
                    <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 7, padding: '8px 4px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <i className="material-icons" style={{ fontSize: 18, color: '#888', marginBottom: 3 }}>{STAT_ICON[s.label] || 'info'}</i>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{s.value}</span>
                      <span style={{ fontSize: 11, color: '#888', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Carousels ─────────────────────────────── */}
          <div className="home-rows" style={{ paddingTop: 8 }}>
            {!isLoading && groups.length === 0 && (
              <div className="warning-wrapper empty-result">
                <div className="error-text">
                  <h2 className="text-center">Sorry !</h2>
                  <p>No content available for this artist.</p>
                </div>
              </div>
            )}
            {groups.map((group, i) => (
              <ArtistCarousel key={group.name || i} groupName={getGroupName(group)} artistId={artistId} />
            ))}
          </div>

        </main>
        <Footer />
      </div>
    </React.Fragment>
  );
}
